import { Col, InputProps, InputRef, Row, Spin } from 'antd';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import { flushSync } from 'react-dom';
import { kebabCase } from 'lodash';
import {
    ChangeEventHandler,
    forwardRef,
    ReactNode,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import IconTooltip from 'components/new-ui/base/tooltip/IconTooltip';
import { TextSmall } from 'components/ui/base/typography';
import useDeclarationInputFocused from 'hooks/useDeclarationInputFocused';
import { getFormikProps, toPlaceholder } from 'views/declarations/utils/form-utils';
import SearchCustomer from 'views/declarations/common/search-customer/SearchCustomer';
import { TemplateCheckboxes } from 'components/ui/composed/template/TemplateCheckbox';
import useDeclarations from 'hooks/useDeclarations';
import { TestProps } from 'core/utils/testTypes';
import MultipleButtons from 'components/ui/composed/declarations/MultipleButtons';
import { useDeclarationValueSource } from 'components/ui/composed/declarations/source/DeclarationSourceContext';
import DeclarationValueSourceIcon from 'components/ui/composed/declarations/source/DeclarationValueSourceIcon';
import { useRegisterField } from 'components/ui/composed/template/CardContext';
import { useTemplateContext } from 'components/ui/composed/template/TemplateContext';
import useFieldTemplateMetaData from 'components/ui/composed/template/useTemplateViewEditControls';
import useNonAmendableField from 'views/declarations/utils/useNonAmendableField';
import SearchCustomerButton from 'views/declarations/common/search-customer/SearchCustomerButton';
import { SuccessLabel } from 'components/ui/composed/formInput/FormInput.styles';
import {
    FormItem,
    InputDiv,
    LabelDiv,
    SelectAccountNumbers,
    StyledInput,
    StyledLabel,
    SuccessDiv,
    SuccessIcon,
    ViewOnlyLabel,
} from './styles';
import { StyledRef } from 'components/new-ui/base/ref/styles';
import { ErrorLabel } from '../../formInput/styles';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';

export interface MultipleProps {
    node: ReactNode;
}

export interface FormInputProps extends Omit<InputProps, 'multiple'>, TestProps {
    label?: string;
    tooltip?: string;
    icon?: ReactNode;
    refNumber?: string;
    children?: ReactNode;
    fieldProps?: FieldInputProps<any>;
    fieldMeta?: FieldMetaProps<any>;
    fieldHelper?: FieldHelperProps<any>;
    multiple?: MultipleProps;
    disabled?: boolean;
    required?: boolean;
    maxLength?: number;
    viewOnly?: boolean;
    numbersAndLetters?: boolean;
    searching?: boolean;
    selectAccountNumbers?: () => void;
    eoriSuccess?: boolean;
    refClicked?: (refNumber: string | number) => void;
    isSearching?: boolean;
    fieldChange?: {
        onChange: ChangeEventHandler<HTMLInputElement>;
    };
    onSearchCustomer?: (args: {
        fieldProps?: FieldInputProps<any>;
        fieldMeta?: FieldMetaProps<any>;
        fieldHelpers?: FieldHelperProps<any>;
    }) => void;
    condensed?: boolean;
    multipleF?: {
        onAdd: () => void;
        onRemove: () => void;
        canAdd?: boolean;
        canRemove?: boolean;
    };
    onSearchTaricCode?: () => void;
    normal?: boolean;
    onMakeInvisible?: (name: string | undefined) => void;
    after?: ReactNode;
    specialName?: string; // Used for template meta handle of taxBox34Bis fields as of 13/04/23
}

export interface IconTooltipProps {
    tooltip?: string;
    icon?: ReactNode;
}

const DeclarationInput = forwardRef<InputRef, FormInputProps>(
    (
        {
            label,
            refNumber,
            tooltip,
            fieldMeta: fieldMetaProp,
            fieldProps: fieldPropsProp,
            fieldHelper: fieldHelpersProp,
            multiple,
            disabled,
            required,
            maxLength,
            viewOnly: viewOnlyProp,
            numbersAndLetters,
            selectAccountNumbers,
            eoriSuccess,
            refClicked,
            isSearching,
            fieldChange,
            onSearchCustomer,
            condensed,
            multipleF,
            onSearchTaricCode,
            normal,
            value: valueProp,
            onChange,
            onBlur,
            onMakeInvisible,
            after,
            specialName,
            hidden,
            testId,
            ...inputProps
        },
        ref
    ) => {
        const inputRef = useRef<InputRef | null>(null);
        useImperativeHandle(ref, () => inputRef.current as InputRef, []);

        const { t } = useTranslation('common');
        const { setFocused } = useDeclarationInputFocused();
        const outletContext = useOutletContext<{
            amendment?: boolean;
        }>();
        const location = useLocation();
        const inViewOnly = location.pathname.includes('view-only');
        const { template, templateFormik, form, isViewOnly: isTemplateViewOnly } = useTemplateContext();
        const { declaration } = useDeclarations();

        const [localValue, setLocalValue] = useState<string | undefined | null>(undefined);

        const [selectingCustomer, setSelectingCustomer] = useState(false);

        useRegisterField({ path: fieldPropsProp?.name ?? inputProps.name, required, hidden });
        const { isViewable, isEditable, isInvisible } = useFieldTemplateMetaData(
            fieldPropsProp?.name ?? inputProps.name
        );
        const isNonAmendable = useNonAmendableField(fieldPropsProp?.name ?? inputProps.name);

        const { fieldProps, fieldMeta, fieldHelpers } = useMemo(() => {
            if (template && templateFormik && !normal && form) {
                const f = getFormikProps(`${form}.defaults.${fieldPropsProp?.name}`, templateFormik);
                return { ...f, fieldHelpers: f.fieldHelper };
            }

            return { fieldProps: fieldPropsProp, fieldMeta: fieldMetaProp, fieldHelpers: fieldHelpersProp };
        }, [template, templateFormik, normal, form, fieldPropsProp, fieldMetaProp, fieldHelpersProp]);

        useEffect(() => {
            if (hidden) return;
            if (fieldProps?.value === '' && fieldHelpers) {
                fieldHelpers.setValue(null);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [fieldProps?.value]);

        useEffect(() => {
            const inputEl = inputRef.current?.input;

            if (!inputEl || !fieldHelpers || !fieldProps?.name) return;

            const handleAutofill = () => {
                const newValue = inputEl.value;

                if (newValue && newValue !== fieldProps.value) {
                    fieldHelpers.setValue(newValue);
                }
            };

            const timeout = setTimeout(handleAutofill, 300);

            return () => clearTimeout(timeout);
        }, [fieldHelpers, fieldProps?.name, fieldProps?.value]);

        const handleAllowedChars = useCallback(
            (e: any) => {
                if (numbersAndLetters) {
                    const result = /^([^0-9,A-Z,a-z ])$/.test(e.key);
                    return result && e.preventDefault();
                }
                return e.key;
            },
            [numbersAndLetters]
        );

        const handleRefClick = useCallback(() => {
            if (refClicked) {
                refNumber ? refClicked(refNumber) : refClicked(label ?? '');
            }
        }, [refClicked, refNumber, label]);

        const iconInput = useMemo(() => {
            if (!!isSearching) {
                return <Spin size="small" />;
            }
            return eoriSuccess ? <SuccessIcon /> : <span />;
        }, [isSearching, eoriSuccess]);

        const showTooltip = useMemo(() => tooltip && tooltip.length > 0, [tooltip]);

        const viewOnly = useMemo(() => {
            if (template) return isTemplateViewOnly;
            if (outletContext?.amendment) return isViewable;
            return viewOnlyProp || !isEditable || inViewOnly || declaration?.archived;
        }, [
            template,
            isTemplateViewOnly,
            outletContext?.amendment,
            isViewable,
            viewOnlyProp,
            isEditable,
            inViewOnly,
            declaration?.archived,
        ]);

        const fillButtonsDisabled = useMemo(
            () => template && !isEditable && !isViewable,
            [isEditable, isViewable, template]
        );

        // Handle validation after choosing a customer
        useEffect(() => {
            if (selectingCustomer) {
                fieldProps?.onBlur({ target: { name: fieldProps.name } });
                fieldHelpers?.setTouched(true);
                setSelectingCustomer(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [fieldProps?.value]);

        const value = useMemo(() => {
            if (localValue !== undefined) return localValue;
            return valueProp ?? fieldProps?.value;
        }, [fieldProps?.value, localValue, valueProp]);

        const valueSource = useDeclarationValueSource(fieldPropsProp?.name ?? inputProps.name, fieldMeta);
        const sourceIcon = useMemo(
            () => (valueSource ? <DeclarationValueSourceIcon {...valueSource} /> : undefined),
            [valueSource]
        );

        const handleTemplateCheckboxChange = useMemo(() => {
            return onChange
                ? (value: any) => onChange({ target: { value, name: inputProps.name ?? '' } } as any)
                : fieldChange?.onChange
                ? (value: any) =>
                      fieldChange.onChange({
                          target: { value, name: inputProps.name ?? '' },
                      } as any)
                : undefined;
        }, [fieldChange, inputProps.name, onChange]);

        const render = useMemo(
            () => (
                <>
                    {viewOnly ? (
                        <>
                            <ViewOnlyLabel>{label && label}:</ViewOnlyLabel>
                            {template && !normal && <TemplateCheckboxes disabled fieldPath={fieldPropsProp?.name} />}
                            {sourceIcon}
                            <TextSmall data-testid={testId ?? kebabCase(label ?? fieldPropsProp?.name)}>
                                {value ?? '-'}
                            </TextSmall>
                        </>
                    ) : (
                        <Row
                            style={{
                                width: '100%',
                            }}
                            justify="space-between"
                            align="middle"
                        >
                            <Col span={10}>
                                <LabelDiv condensed={condensed}>
                                    {refNumber && <StyledRef>{refNumber}</StyledRef>}

                                    {label && (
                                        <StyledLabel htmlFor={fieldProps?.name}>
                                            {required && <LabelRequiredSymbol />}
                                            <span>{label}</span>
                                            {showTooltip && <IconTooltip title={label} onClick={handleRefClick} />}
                                            <span>:</span>
                                        </StyledLabel>
                                    )}
                                </LabelDiv>
                                {selectAccountNumbers && (
                                    <SelectAccountNumbers onClick={() => selectAccountNumbers()}>
                                        {t('selectFromCustomer')}
                                    </SelectAccountNumbers>
                                )}

                                {template && !normal && (
                                    <TemplateCheckboxes
                                        onMakeInvisible={onMakeInvisible}
                                        required={required}
                                        fieldPath={fieldPropsProp?.name ?? inputProps.name}
                                        specialName={specialName}
                                        onChange={handleTemplateCheckboxChange}
                                    />
                                )}
                            </Col>

                            <Col span={14}>
                                <FormItem validateStatus={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}>
                                    <InputDiv>
                                        <StyledInput
                                            id={fieldProps?.name}
                                            ref={inputRef}
                                            showSuccess={eoriSuccess ?? false}
                                            placeholder={inputProps.placeholder ?? toPlaceholder(label) ?? ''}
                                            prefix={sourceIcon}
                                            suffix={iconInput}
                                            disabled={disabled || isInvisible || isNonAmendable}
                                            testId={testId ?? kebabCase(label ?? fieldPropsProp?.name)}
                                            maxLength={maxLength}
                                            onKeyDown={handleAllowedChars}
                                            status={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}
                                            name={inputProps.name}
                                            size={condensed ? 'middle' : 'large'}
                                            condensed={condensed}
                                            {...fieldProps}
                                            value={value}
                                            onFocus={(e) => {
                                                setFocused(true);
                                                setLocalValue(e.target.value ?? null);
                                            }}
                                            onBlur={(e) => {
                                                flushSync(() => {
                                                    if (onChange) onChange(e);
                                                    else if (fieldChange?.onChange) fieldChange.onChange(e);
                                                    else fieldProps?.onChange(e);
                                                });
                                                setFocused(false);
                                                setLocalValue(undefined);
                                                if (onBlur) onBlur(e);
                                                else fieldProps?.onBlur(e);
                                                fieldHelpers?.setTouched(true);
                                            }}
                                            onChange={(e) => setLocalValue(e.target.value)}
                                        />
                                        {multiple ? multiple.node : <></>}
                                        {multipleF && <MultipleButtons {...multipleF} />}
                                        {(!isViewable && !template) ||
                                            (!viewOnly && onSearchCustomer && (
                                                <SearchCustomer.Button
                                                    compact
                                                    disabled={fillButtonsDisabled}
                                                    condensed={condensed}
                                                    onClick={() => {
                                                        onSearchCustomer({ fieldProps, fieldMeta, fieldHelpers });
                                                        setSelectingCustomer(true);
                                                    }}
                                                />
                                            ))}

                                        {!viewOnly && onSearchTaricCode && (
                                            <SearchCustomerButton
                                                compact
                                                disabled={fillButtonsDisabled}
                                                condensed={condensed}
                                                title=" Look up taric codes"
                                                onClick={() => onSearchTaricCode()}
                                            />
                                        )}
                                        {after}
                                    </InputDiv>
                                    {!eoriSuccess ? (
                                        <div>
                                            {!!fieldMeta?.error && !!fieldMeta?.touched && (
                                                <ErrorLabel>{fieldMeta?.error}</ErrorLabel>
                                            )}
                                        </div>
                                    ) : (
                                        <SuccessDiv>
                                            <SuccessLabel>The EORI code is valid!</SuccessLabel>
                                        </SuccessDiv>
                                    )}
                                </FormItem>
                            </Col>
                        </Row>
                    )}
                </>
            ),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [
                fieldMeta?.error,
                fieldMeta?.touched,
                fieldMeta?.value,
                value,
                disabled,
                onBlur,
                onChange,
                iconInput,
                viewOnly,
                multipleF?.canRemove,
                multipleF?.canAdd,
                eoriSuccess,
                sourceIcon,
            ]
        );

        if (hidden || (isInvisible && !template)) return null;

        return render;
    }
);

export default DeclarationInput;
